// Team-Mitglied einer Firma, deren Tarif kein Team (mehr) enthält: Jeder Zugriff auf die Firma antwortet mit 402
// tarif_feature und details.ruht (server billtoro/routes.ts). Statt einer Fehlermeldung diese ruhige Seite: was los
// ist, wer helfen kann, und was trotzdem geht (andere Firma, Konto, Firma verlassen, Abmelden). Das Rechnungswerkzeug
// zeigt sie an Stelle der Bereiche, wenn schon die Übersicht so abgelehnt wird (rechnung/RechnungTool.tsx). Verlassen
// geht auch, solange der Zugang ruht (POST /api/billtoro/:id/team/verlassen).
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { useSession } from '../auth/session';
import { ConfirmDialog } from '../components/Dialog';
import type { Me } from '../types';
import { ruhtAngezeigt } from './ruht';
import './hinweise.css';

// Ist dieser Fehler „Zugang ruht“? Genau 402 tarif_feature mit details.ruht (nicht jede fehlende Funktion)
export function istRuht(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 402 || err.code !== 'tarif_feature') return false;
  const details = (err.data as { details?: { ruht?: unknown } } | undefined)?.details;
  return details?.ruht === true;
}

export function ZugangRuht({
  me,
  firmId,
  text,
  onFirma,
}: {
  me: Me;
  firmId: string | null;
  // Text vom Server (sonst ein eigener)
  text?: string | null;
  // andere Firma wählen (Adresse bleibt)
  onFirma?(id: string): void;
}) {
  const { logout, refresh } = useSession();
  const navigate = useNavigate();
  const [frage, setFrage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Solange diese Seite steht, zeigt das Fenster „Mehr mit Billtoro“ den Hinweis nicht noch einmal
  useEffect(() => ruhtAngezeigt(), []);
  const firma = me.clients.find((c) => c.id === firmId)?.name;
  const andere = me.clients.filter((c) => c.status === 'active' && c.id !== firmId);

  async function verlassen() {
    if (!firmId) return;
    setBusy(true);
    setFehler(null);
    try {
      await api.post(`/api/billtoro/${firmId}/team/verlassen`);
      setFrage(false);
      // War es die einzige Firma, ist das Login gelöscht: refresh endet ohne Sitzung, die App geht zur Anmeldung
      await refresh().catch(() => null);
      const naechste = andere[0];
      if (naechste && onFirma) onFirma(naechste.id);
      else navigate('/rechnung', { replace: true });
    } catch (err) {
      setFrage(false);
      setFehler(errorText(err));
      setBusy(false);
    }
  }

  return (
    <section className="bt-ruht" aria-labelledby="bt-ruht-titel">
      <div className="bt-ruht-zeichen" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M10 9v6M14 9v6" />
        </svg>
      </div>
      <h1 id="bt-ruht-titel">Ihr Zugang ruht</h1>
      <p className="bt-ruht-text">
        {text ??
          'Ihr Zugang zu dieser Firma ruht, bis sie den Tarif Pro bucht. Bitte wenden Sie sich an den Inhaber.'}
      </p>
      <p className="muted bt-ruht-klein">
        {firma ? <>Firma: {firma}. </> : null}
        Es ist nichts verloren: Sobald die Firma wieder ein Team hat, arbeiten Sie hier weiter wie zuvor.
      </p>
      <div className="bt-ruht-akt">
        {onFirma &&
          andere.map((f) => (
            <button key={f.id} type="button" className="btn primary" onClick={() => onFirma(f.id)}>
              Zu {f.name} wechseln
            </button>
          ))}
        <Link to="/rechnung/konto" className="btn">
          Mein Konto
        </Link>
        <button type="button" className="btn" onClick={() => void logout()}>
          Abmelden
        </button>
      </div>
      {firmId && (
        <p className="bt-ruht-verlassen">
          <button type="button" className="linkbtn" onClick={() => setFrage(true)}>
            Firma verlassen
          </button>
        </p>
      )}
      {fehler && (
        <p className="notice bad" role="alert">
          {fehler}
        </p>
      )}
      <ConfirmDialog
        open={frage}
        title={`${firma ?? 'Firma'} verlassen?`}
        text={
          andere.length
            ? 'Sie haben danach keinen Zugang mehr zu dieser Firma. Der Inhaber kann Sie später wieder einladen.'
            : 'Sie haben danach keinen Zugang mehr zu dieser Firma. Weil es Ihre einzige Firma bei Billtoro ist, wird Ihr Login gelöscht. Der Inhaber kann Sie später wieder einladen.'
        }
        confirmLabel="Verlassen"
        danger
        busy={busy}
        onConfirm={() => void verlassen()}
        onCancel={() => setFrage(false)}
      />
    </section>
  );
}
